import { useCallback, useState } from 'react';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { useQuery } from 'react-query';

import Button from '../Button';
import { Loader } from '../Loader';

import { ItemAssistant } from './ItemsAssistant';
import { ReferencesFieldAssistance } from './ReferencesFieldAssistance';

import { useIsDesktop } from '@/hooks/useIsDesktop';
import { getHistoric, getHistoricF5 } from '@/services/fieldAssistant';
import { DateGDT } from '@/types';
import { MyPlayersScore } from '@/types/fieldAssistance';
import { EnumTeamContext, VariantFieldAssistance } from '@/types/team';
import { cn } from '@/utils';

interface StateTypes {
  matchDay: DateGDT;
  counter: number;
  disablingPrev: boolean;
  disablingNext: boolean;
}

interface Props extends VariantFieldAssistance {
  context?: EnumTeamContext;
  matchsDaysAvailables: DateGDT[];
  isF5: boolean;
  idUltFechaPuntos: number | undefined;
}

type StateData = {
  error: boolean;
  msg: string;
  team: MyPlayersScore[] | undefined;
};

const noData = {
  msg: 'La competencia no inició. Tus jugadores no sumaron puntos',
  team: undefined,
  error: true,
};

const noDataNewUser = {
  msg: 'No participaste de la última fecha',
  team: undefined,
  error: true,
};

export const ScoreMyPlayersTable = ({
  variant,
  matchsDaysAvailables,
  isF5,
  idUltFechaPuntos,
}: Props) => {
  const initialValueState: StateTypes = {
    matchDay: matchsDaysAvailables.slice(-1)[0] || {
      id: 0,
      isPublicada: false,
      nombre: '',
      orden: 0,
    },
    counter: matchsDaysAvailables.length - 1 || 0,
    disablingPrev: matchsDaysAvailables.length <= 1,
    disablingNext: true,
  };
  const [stateDate, setStateDate] = useState(initialValueState);
  const [stateData, setStateData] = useState<StateData>({
    error: false,
    msg: '',
    team: undefined,
  });

  const { isLoading, isRefetching } = useQuery(
    ['fechaId', stateDate.matchDay.id || 0],
    async (matchDay) => {
      if (idUltFechaPuntos) {
        const fechaParamId =
          matchDay.queryKey[1] !== 0
            ? parseInt(matchDay.queryKey[1].toString())
            : undefined;
        if (fechaParamId) {
          if (matchsDaysAvailables.length === 0) {
            setStateData(noDataNewUser);
          } else {
            const response = isF5
              ? await getHistoricF5(fechaParamId)
              : await getHistoric(fechaParamId);
            if (response.estado) {
              setStateData({
                msg: '',
                team: response.miEquipoPtos,
                error: false,
              });
            } else {
              if (response.codError && response.error && response.error?.msg) {
                setStateData({
                  msg: response.error?.msg,
                  team: undefined,
                  error: true,
                });
              }
            }
          }
        } else {
          setStateData(noDataNewUser);
        }
      } else {
        setStateData(noData);
      }
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
    }
  );

  const { isMobile, isDesktop } = useIsDesktop();

  const handlePrevMatch = useCallback(() => {
    if (stateDate.disablingPrev) return;
    setStateDate({
      ...stateDate,
      disablingNext: false,
      disablingPrev: stateDate.counter <= 1,
      counter: !(stateDate.counter === 0)
        ? stateDate.counter - 1
        : stateDate.counter,
      matchDay: Object.values(matchsDaysAvailables)[stateDate.counter - 1],
    });
  }, [matchsDaysAvailables, stateDate]);

  const handleNextMatch = useCallback(() => {
    if (stateDate.disablingNext) return null;
    setStateDate({
      ...stateDate,
      disablingPrev: false,
      disablingNext: stateDate.counter + 2 === matchsDaysAvailables.length,
      counter: !(stateDate.counter === matchsDaysAvailables.length - 1)
        ? stateDate.counter + 1
        : stateDate.counter,
      matchDay: Object.values(matchsDaysAvailables)[stateDate.counter + 1],
    });
  }, [matchsDaysAvailables, stateDate]);

  return (
    <>
      <Loader isVisible={isLoading || isRefetching} timeout={isRefetching} />
      <>
        {!stateData.error && (
          <>
            {isDesktop && (
              <div className="items-center bg-transparent lg:rounded-tl-lg lg:rounded-tr-lg lg:bg-gdt-blue-xl2 h-8 lg:h-[3.3rem] mb-4 lg:mb-0 justify-center mt-4 lg:mt-6 hidden lg:flex">
                <Button
                  className={cn('w-[1.6875rem] h-[1.6875rem] p-0', {
                    'bg-white border-0 text-gdt-gray-5 cursor-not-allowed':
                      stateDate.disablingPrev,
                  })}
                  disabled={isLoading || isRefetching}
                  type="button"
                  variant="primary"
                  onClick={handlePrevMatch}
                >
                  <IoIosArrowBack className="m-auto" size={19} />
                </Button>
                <p className="text-gdt-blue text-base lg:text-lg font-bold mx-5 min-w-[76px]">
                  {stateDate.matchDay.nombre}
                </p>
                <Button
                  className={cn('w-[1.6875rem] h-[1.6875rem] p-0', {
                    'bg-white border-0 text-gdt-gray-5 cursor-not-allowed':
                      stateDate.disablingNext,
                  })}
                  disabled={isLoading || isRefetching}
                  type="button"
                  variant="primary"
                  onClick={handleNextMatch}
                >
                  <IoIosArrowForward className="m-auto" size={19} />
                </Button>
              </div>
            )}
            <div className="flex justify-between items-center lg:p-0 px-4 mt-4 lg:mt-0 h-10 lg:bg-white lg:justify-end lg:border-b-[1px] lg:border-b-gdt-gray-7">
              <p className="text-sm lg:text-xs text-gdt-gray-2 font-bold lg:hidden">
                Lista de Jugadores
              </p>
              <div className="flex max-w-[100px] lg:max-w-[244px] w-full text-2xs text-gdt-gray-4 font-semibold text-center">
                <p className="flex-1 text-center lg:text-center leading-tight">
                  {isMobile ? 'Ptos. Gran DT' : 'Puntos Gran DT'}
                </p>
                <p className="flex-1 text-right leading-tight lg:text-center">
                  {isMobile ? 'Ptos. Clarín' : 'Puntos Clarín'}
                </p>
              </div>
            </div>
          </>
        )}
        {stateData.team && (
          <>
            <div
              className={cn('lg:min-h-[944px] bg-white mt-5 lg:mt-0', {
                'overflow-auto h-[calc(100vh-180px)]': variant === 'EMBED',
                'lg:min-h-[auto]': isF5,
              })}
            >
              {stateData.team.map((player: MyPlayersScore) => {
                return (
                  <ItemAssistant.Incidents key={player.id} player={player} />
                );
              })}
            </div>
            {isDesktop && <ReferencesFieldAssistance />}
          </>
        )}
        {stateData.error && !stateData.team && (
          <p className="px-4 py-6 mt-4 bg-white">{stateData.msg}</p>
        )}
      </>
    </>
  );
};
